import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Star,
  Pause,
  Play,
  Copy,
  Archive,
  Edit,
  MoreHorizontal,
  Search,
  ChevronDown,
  GitBranch,
  Users,
  CheckCircle2,
  Workflow,
  Rocket,
  Mail,
  Clock,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import {
  useSequencesQuery,
  usePauseSequenceMutation,
  useActivateSequenceMutation,
  useDuplicateSequenceMutation,
  useArchiveSequenceMutation,
  useStarSequenceMutation,
  useUnstarSequenceMutation,
  type Sequence,
} from "@/hooks/useSequences";
import { useWorkflowsQuery, getWorkflowSendingStatus } from "@/hooks/useWorkflows";

const tabs = ["All", "Active", "Paused", "Draft", "Completed"];
const sortOptions = ["Last Modified", "Name A-Z", "Name Z-A", "Date Created"];

const statusBadgeClass = (status: string): string => {
  switch (status.toLowerCase()) {
    case "active":
      return "sq-badge-green";
    case "paused":
      return "sq-badge-amber";
    case "draft":
      return "sq-badge-gray";
    case "completed":
      return "sq-badge-completed";
    default:
      return "sq-badge-gray";
  }
};

const mapSortBy = (sortBy: string): string => {
  switch (sortBy) {
    case "Last Modified":
      return "created_desc";
    case "Name A-Z":
      return "name_asc";
    case "Name Z-A":
      return "name_desc";
    case "Date Created":
      return "created_asc";
    default:
      return "created_desc";
  }
};

export default function SequencesPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("Last Modified");
  const [archiveTarget, setArchiveTarget] = useState<Sequence | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounce search input 300ms
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  const { data, isLoading } = useSequencesQuery({
    status: activeTab === "All" ? undefined : activeTab.toLowerCase(),
    search: debouncedSearch || undefined,
    sort_by: mapSortBy(sortBy),
  });

  const pauseMutation = usePauseSequenceMutation();
  const activateMutation = useActivateSequenceMutation();
  const duplicateMutation = useDuplicateSequenceMutation();
  const archiveMutation = useArchiveSequenceMutation();
  const starMutation = useStarSequenceMutation();
  const unstarMutation = useUnstarSequenceMutation();

  const { data: allWorkflowsData } = useWorkflowsQuery({ per_page: 500 });
  const lockedSequenceIds = useMemo(() => {
    const ids = new Set<string>();
    for (const wf of allWorkflowsData?.data?.workflows ?? []) {
      const s = getWorkflowSendingStatus(wf);
      if (s !== null && s !== 'pending' && s !== 'done' && wf.sequence_id) {
        ids.add(wf.sequence_id);
      }
    }
    return ids;
  }, [allWorkflowsData]);

  const rawSequences = data?.data?.sequences ?? [];
  const sequences = [...rawSequences].sort((a, b) => {
    const aStarred = a.starred === true ? 0 : 1;
    const bStarred = b.starred === true ? 0 : 1;
    return aStarred - bStarred;
  });
  const counts = data?.data?.counts;

  const handleToggleStar = useCallback((seq: Sequence) => {
    if (seq.starred) {
      unstarMutation.mutate(seq.id);
    } else {
      starMutation.mutate(seq.id);
    }
  }, [starMutation, unstarMutation]);

  const emailCount = (steps: Sequence["steps"]): number => steps.length;

  const handlePause = (seq: Sequence) => {
    pauseMutation.mutate(seq.id, {
      onSuccess: () => toast.success("Sequence paused"),
      onError: () => toast.error("Failed to pause sequence"),
    });
  };

  const handleResume = (seq: Sequence) => {
    activateMutation.mutate(seq.id, {
      onSuccess: () => toast.success("Sequence resumed"),
      onError: (err) => toast.error((err as Error).message || "Failed to resume sequence"),
    });
  };

  const handleDuplicate = (seq: Sequence) => {
    duplicateMutation.mutate(seq.id, {
      onSuccess: () => toast.success("Sequence duplicated"),
      onError: () => toast.error("Failed to duplicate sequence"),
    });
  };

  const handleArchiveConfirm = () => {
    if (!archiveTarget) return;
    archiveMutation.mutate(archiveTarget.id, {
      onSuccess: () => {
        toast.success("Sequence archived");
        setArchiveTarget(null);
      },
      onError: () => {
        toast.error("Failed to archive sequence");
        setArchiveTarget(null);
      },
    });
  };

  const statusLabel = (status: string): string => status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <div className="portal-page sq-page">
      <div className="sq-header">
        <h1 className="sq-title">Sequences</h1>
        <div className="sq-header-buttons">
          <Link to="/app/workflows/new" className="btn btn-primary btn-sm">
            <Workflow size={16} /> Build Workflow
          </Link>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate("/app/sequences/new")}>
            <Plus size={16} /> New Sequence
          </button>
        </div>
      </div>

      <div className="sq-toolbar">
        <div className="sq-tabs">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`sq-tab ${activeTab === tab ? "sq-tab--active" : ""}`}
            >
              {tab}
              {tab !== "All" && counts && (
                <span className="sq-tab-count">{counts[tab.toLowerCase() as keyof typeof counts] ?? 0}</span>
              )}
            </button>
          ))}
        </div>
        <div className="sq-toolbar-right">
          <div className="sq-search-wrap">
            <Search size={16} className="sq-search-icon" />
            <input
              type="text"
              placeholder="Search sequences..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="sq-search-input"
            />
          </div>
          <div className="sq-sort-wrap">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sq-sort-select">
              {sortOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="sq-sort-chevron" />
          </div>
        </div>
      </div>

      <div className="sq-list">
        {isLoading && <div className="sq-loading">Loading sequences…</div>}
        {!isLoading && sequences.length === 0 && (
          <div className="sq-empty-state">
            <MessageSquare size={40} className="sq-empty-icon" />
            <p className="sq-empty-title">No sequences found.</p>
            <p className="sq-empty-desc">Create your first sequence to get started.</p>
          </div>
        )}
        {sequences.map((seq: Sequence) => (
          <div key={seq.id} className="card sq-card">
            <div className="sq-card-header">
              <div className="sq-card-info">
                <div className="sq-card-name-row">
                  <span className={`sq-badge ${statusBadgeClass(seq.status)}`}>
                    {seq.status === "active" && <span className="sq-pulse-dot" />}
                    {statusLabel(seq.status)}
                  </span>
                  <button
                    className={`sq-star-btn ${seq.starred === true ? "sq-star-btn--active" : ""}`}
                    onClick={() => handleToggleStar(seq)}
                  >
                    <Star size={16} fill={seq.starred === true ? "var(--amber)" : "none"} />
                  </button>
                  <Link to={`/app/sequences/${seq.id}?tab=Sequences`} className="sq-card-link">
                    <h3 className="sq-card-title">{seq.name}</h3>
                  </Link>
                </div>
                <div className="sq-card-subtitle">
                  <GitBranch size={14} />
                  <span>
                    {emailCount(seq.steps)} emails · Created{" "}
                    {new Date(seq.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}{" "}
                    {new Date(seq.created_at).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}{" "}
                    · Last modified{" "}
                    {new Date(seq.updated_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}{" "}
                    {new Date(seq.updated_at).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </span>
                  <span className="sq-card-stat">
                    <Users size={12} /> 0 active
                  </span>
                  <span className="sq-card-stat">
                    <CheckCircle2 size={12} /> 0 done
                  </span>
                </div>
              </div>
              <div className="sq-card-actions">
                {seq.status === "active" && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePause(seq)}
                    disabled={pauseMutation.isPending}
                  >
                    <Pause size={14} /> Pause
                  </button>
                )}
                {seq.status === "paused" && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleResume(seq)}
                    disabled={activateMutation.isPending}
                  >
                    <Play size={14} /> Resume
                  </button>
                )}
                {seq.status === "draft" && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => navigate(`/app/sequences/${seq.id}?tab=Sequences`)}
                  >
                    <Rocket size={14} /> Launch
                  </button>
                )}
                {!lockedSequenceIds.has(seq.id) && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate(`/app/sequences/${seq.id}?tab=Sequences`)}
                  >
                    <Edit size={14} /> Edit
                  </button>
                )}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleDuplicate(seq)}
                  disabled={duplicateMutation.isPending}
                >
                  <Copy size={14} /> Duplicate
                </button>
                <div className="sq-overflow-wrap" ref={openMenuId === seq.id ? menuRef : null}>
                  <button
                    className="btn btn-secondary btn-sm sq-more-btn"
                    onClick={() => setOpenMenuId(openMenuId === seq.id ? null : seq.id)}
                  >
                    <MoreHorizontal size={16} />
                  </button>
                  {openMenuId === seq.id && (
                    <div className="sq-dropdown">
                      <button
                        className="sq-dropdown-item sq-dropdown-item--danger"
                        onClick={() => { setOpenMenuId(null); setArchiveTarget(seq); }}
                      >
                        <Archive size={14} /> Archive
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="sq-steps">
              {seq.steps.map((step: import("@/hooks/useSequences").SequenceStep, idx: number) => {
                const nextStep = seq.steps[idx + 1];
                const dc = nextStep?.delay_config;
                const amount = dc ? (dc.amount ?? dc.value) : null;
                const unit =
                  dc && typeof dc.unit === "string"
                    ? dc.unit.charAt(0).toUpperCase() + dc.unit.slice(1).toLowerCase()
                    : "Days";
                return (
                  <div key={step.id} className="sq-step sq-step--email">
                    <div className="sq-step-icon sq-step-icon--email">
                      <Mail size={13} />
                    </div>
                    <span className="sq-step-label">{step.step_label}</span>
                    {typeof amount === "number" && (
                      <span className="sq-step-delay">
                        <Clock size={10} />
                        {amount} {unit}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Archive confirmation dialog */}
      {archiveTarget && (
        <div className="sq-confirm-overlay">
          <div className="card sq-confirm-card">
            <h3 className="sq-confirm-title">Archive Sequence</h3>
            <p className="sq-confirm-text">
              Are you sure you want to archive &ldquo;{archiveTarget.name}&rdquo;? This action cannot be undone.
            </p>
            <div className="sq-confirm-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setArchiveTarget(null)}>
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleArchiveConfirm}
                disabled={archiveMutation.isPending}
              >
                <Archive size={14} /> Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
